import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { enableMfa } from '@global/api'
import * as SnackbarActions from '@global/snackbar/snackbar.actions'
import { KEY as MFA_CHECK_QUERY_KEY } from './useQueryMfaCheck'
import { useMfaStore } from '../store/useMfaStore'
import { AxiosError } from 'axios'

interface useEnableMfaProps {
  email: string
  token: string
  code: string
  secret: string
  onSuccess?: () => void
}

export const useEnableMfa = ({ email, token, code, secret, onSuccess }: useEnableMfaProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { setIsSetupSuccessful } = useMfaStore()
  const history = useHistory()

  const handleSubmit = async e => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    const response = await enableMfa({
      email,
      mfa_token: token,
      secret,
      code,
    }).catch((err: unknown) => {
      if (
        err instanceof AxiosError &&
        err.response?.status === 403 &&
        err.response?.data?.error === 'INVALID_MFA_TOKEN'
      ) {
        setTimeout(() => {
          history.push('/login')
        }, 2500)

        return { error: 'Your session got expired, please try again' }
      }

      return err
    })

    if (response.error) {
      dispatch(
        SnackbarActions.showSnackbar({
          type: 'ERROR',
          message: response.error || 'Could not enable Multi Factor Authentication.',
        }),
      )
      setIsLoading(false)
      return
    }

    setIsLoading(false)
    setIsSetupSuccessful(true)

    dispatch(
      SnackbarActions.showSnackbar({
        type: 'SUCCESS',
        message: 'Multi-Factor Authentication successfuly enabled!',
        duration: 5000,
      }),
    )

    void queryClient.invalidateQueries({ queryKey: [MFA_CHECK_QUERY_KEY, email] })
    onSuccess?.()
  }

  return {
    handleSubmit,
    isLoading,
    error,
  }
}
